<template>
  <div>
    <b-container class="py-3">
      <b-row>
        <b-col>
          <b-alert v-if="inactiveSubscription" show class="mb-3" variant="danger">
            <h4>Expired Subscription</h4>
            <p>
              Your subscription has expired. You will not be able to invite new clients until you <b-link to="/subscribe">renew your subscription</b-link>.
            </p>
            <hr />
            <b-button to="/subscriber/manageSubscription" variant="primary">Manage My Subscription</b-button>
          </b-alert>

          <page-title title="Add Client" />
          <hr />

          <b-form novalidate @submit.prevent="onSubmit">
            <text-control label="First Name" class="w-50" v-model="item.firstName" required></text-control>
            <text-control label="Last Name" class="w-50" v-model="item.lastName" required></text-control>

            <h4 class="mt-3">In Custody / Proctored</h4>
            <checkbox-control label="This client is in custody, and I will proctor the questionnaire to them." description="Only questionnaires for proctored clients can be checked out, making them available for offline access." v-model="item.isProctored"></checkbox-control>

            <div v-if="!item.isProctored">

              <h4 class="mt-3">Email Invitation</h4>
              <checkbox-control label="Send Email Invitation" v-model="item.useEmail"></checkbox-control>
              <text-control label="Email Address" class="w-50" v-model="item.email" :custom-validation="validateEmail" type="text"></text-control>

              <h4 class="mt-3">Text Message Invitation</h4>
              <checkbox-control label="Send Text Message Invitation" v-model="item.useSms"></checkbox-control>
              <text-control label="Phone Number" class="w-50" v-model="item.phoneNumber" :custom-validation="validatePhone" type="tel" placeholder="000-000-0000" @input.native.capture="formatPhone"></text-control>

              <h4 class="mt-3">Reminder</h4>
              <p>A reminder message will be sent to your client if they do not complete the survey within 3 days.</p>
              <radio-button-list-control label="Reminder Message" class="w-50" :options="reminderDaysOptions" name="Reminder" v-model="item.reminderDays" />

            </div>

            <h4 class="mt-3">Additional Assessments</h4>
            <b-form-checkbox v-for="template in optionalTemplates" :key="template.id" v-model="template.checked" :checked-value="true" :unchecked-value="false">{{template.subscriberTitle}}</b-form-checkbox>

            <h4 class="mt-3">Supporter Documents</h4>
            <p>If enabled, clients can request letters of support from family and friends.</p>
            <checkbox-control label="Allow Client to Request Letters of Support" v-model="item.enableSupporters"></checkbox-control>

            <b-button-toolbar key-nav class="mt-3">
              <b-button-group>
                <b-button type="submit" variant="primary" :disabled="isAdding || inactiveSubscription">
                  <b-spinner small v-if="isAdding"></b-spinner>
                  Add Client
                </b-button>
              </b-button-group>
            </b-button-toolbar>
                
          </b-form>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import axios from "axios";
import FormMixin from '../../Mixins/FormMixin.vue';

export default {
  name: "SubscriberClientAdd",
  mixins: [
    FormMixin
  ],
  props: [],
  data() {
    return {
      item: {
        firstName: null,
        lastName: null,
        email: null,
        phoneNumber: null,
        useEmail: true,
        useSms: false,
        reminderDays: 7,
        enableSupporters: true,
        isProctored: false
      },
      templates: [],
      isAdding: false,
      subscriptionUser: null,
      inactiveSubscription: false,
      reminderDaysOptions: [
        {
          text: 'Do not send additional reminder messages',
          value: 0
        },
        {
          text: 'Send an additional reminder message after 5 days',
          value: 5
        },
        {
          text: 'Send an additional reminder message after 7 days',
          value: 7
        },
        {
          text: 'Send an additional reminder message after 10 days',
          value: 10
        }
      ]
    };
  },
  computed: {
    optionalTemplates() {
      return this.templates.filter(x => x.isOptional).sort((a, b) => a.initialDisplayWeight - b.initialDisplayWeight);
    }
  },
  methods: {
    validateEmail: function () {
      if (this.item.useEmail && !this.item.email)
        return 'Please provide an email address in order to send an email invitation.';

      if (this.item.useEmail && !this.item.email.match(/(.+)@(.+){2,}\.(.+){2,}/))
        return 'Please provide a valid email address.';

      return true;
    },
    formatPhone(event) {
      const input = event.target;
      const digitsBeforeCursor = input.value.slice(0, input.selectionStart).replace(/\D/g, '').length;
      const digits = input.value.replace(/\D/g, '');
      input.value = digits.replace(/^(\d{3})(\d)/, '$1-$2').replace(/^(\d{3}-\d{3})(\d)/, '$1-$2');
      let cursor = 0;
      let count = 0;
      while (cursor < input.value.length && count < digitsBeforeCursor) {
        if (/\d/.test(input.value[cursor])) count++;
        cursor++;
      }
      input.setSelectionRange(cursor, cursor);
    },
    validatePhone: function () {
      if (this.item.useSms && !this.item.phoneNumber)
        return 'Please provide a phone number in order to send a text message invitation.';
      
      if (this.item.useSms && !this.item.phoneNumber.match(/^\d{3}-\d{3}-\d{4}$/))
        return 'Please format the phone number as XXX-XXX-XXXX';

      return true;
    },
    loadSubscriptionUser: function () {
      axios({
        url: '/api/subscriptionUser?includes=subscription&filter=' + encodeURIComponent('applicationUserId="' + this.$root.$data.authenticatedUser.id + '"'),
        method: 'GET'
      }).then((response) => {
        if (!response.data || response.data.length < 1) {
          this.subscriptionUser = null;
          this.inactiveSubscription = true;
        } else {
          this.subscriptionUser = response.data[0];
          this.inactiveSubscription = !this.subscriptionUser.subscription.isActive;
        }
      });
    },
    load() {
      axios
        .get('/api/template?limit=100')
        .then(response => {
          console.log(response);

          this.templates = response.data;
          this.templates.forEach(x => x.checked = x.isDefault);
        })
        .catch(error => {
          console.log(error);
        });
    },
    onSubmit(evt) {
      if (this.isAdding) return;

      if (!this.isFormValid()) {
        this.$parent.showToast('Please make sure you have completed all the fields for the new client.', {
          title: 'Please Provide Additional Information',
          variant: 'danger',
          autoHideDelay: 5000
        });

        return;
      }

      this.isAdding = true;
      
      this.item.templateIds = this.templates.filter(x => x.checked).map(x => x.id);

      axios
        .post('/api/client/invite', this.item)
        .then(response => {
          console.log(response);

          const data = response.data;
          const failed = [];
          if (data.invitationEmailStatus === 'Failed') failed.push('Email');
          if (data.invitationSmsStatus === 'Failed') failed.push('SMS');
          const submitted = [];
          if (data.invitationEmailStatus === 'Submitted') submitted.push('Email');
          if (data.invitationSmsStatus === 'Submitted') submitted.push('SMS');
          let message = 'Client created.';
          if (submitted.length) message += ' ' + submitted.join(' and ') + ' invitation submitted.';
          if (failed.length) message += ' ' + failed.join(' and ') + ' failed. Do not add this client again.';

          this.$parent.showToast(message, {
            title: failed.length ? 'Client Created — Invitation Incomplete' : 'Client Created',
            variant: failed.length ? 'warning' : 'success',
            autoHideDelay: failed.length ? 15000 : 5000
          });

          this.isAdding = false;

          this.$router.push('/');
        })
        .catch(error => {
          console.log(error);

          let errorMessage = 'The request could not be confirmed. Check your client list before adding this client again.';

          const status = error.response && error.response.status;
          if (status == 400) {
            errorMessage = 'Unable to invite ' + this.item.firstName + ' ' + this.item.lastName + '. ';
            if (error.response.data.Details)
              errorMessage += error.response.data.Details;
            else
              errorMessage += 'The data you provided could not be validated.';
          } else if (status == 403)
            errorMessage = 'Unable to invite ' + this.item.firstName + ' ' + this.item.lastName + '. You are not logged in or do not have access.';
          else if (status == 409)
            errorMessage = 'Unable to save "' + this.item.name + '". The item has been edited since you loaded the page. Reload this page to get the latest changes.';

          this.$parent.showToast(errorMessage, {
            title: 'Error Inviting Client',
            variant: 'danger',
            autoHideDelay: 5000
          });

          this.isAdding = false;
        });
    }    
  },
  mounted () {
    this.load();
    this.loadSubscriptionUser();
    // Use this instead of the previous line to test the "Loading" bar
    //window.setTimeout(this.load, 3000);
  }
};
</script>